interface ArticleCategory {
  term_id: number
  name: string
  slug: string
}

interface ArticleResponse {
  id: number
  title: string
  categories: ArticleCategory[]
  image_html: string
  image_url: string
  content_html: string
}

const TITLE_ID = 'hg-article-modal-title'

class ArticleModal {
  private overlay: HTMLElement | null = null
  private categoriesEl: HTMLElement | null = null
  private scrollZone: HTMLElement | null = null
  private closeEls: HTMLElement[] = []
  private triggers: HTMLElement[] = []
  private lastFocus: HTMLElement | null = null
  private opened = false
  private isLeaving = false

  init(container: Document | HTMLElement = document) {
    const triggers = Array.from(container.querySelectorAll<HTMLElement>('[data-hg-post-card-read-more]'))
    if (triggers.length === 0) {
      this.removeBodyOverlays()
      return
    }
    if (!this.ensureOverlay(container)) return

    triggers.forEach((el) => {
      el.addEventListener('click', this.onTriggerClick)
      this.triggers.push(el)
    })
  }

  update(container: Document | HTMLElement = document) {
    this.detachTriggers()
    this.detachOverlay()
    this.init(container)
  }

  cleanup() {
    this.detachTriggers()
    this.detachOverlay()
  }

  private detachOverlay() {
    this.closeEls.forEach((el) => el.removeEventListener('click', this.onCloseClick))
    this.closeEls = []
    document.removeEventListener('keydown', this.onKeydown)
    document.body.classList.remove('has-article-modal-open')
    this.overlay?.classList.remove('is-open', 'is-leaving', 'is-loading')
    this.overlay?.setAttribute('aria-hidden', 'true')
    this.overlay = null
    this.categoriesEl = null
    this.scrollZone = null
    this.opened = false
    this.isLeaving = false
  }

  private detachTriggers() {
    this.triggers.forEach((t) => t.removeEventListener('click', this.onTriggerClick))
    this.triggers = []
  }

  private ensureOverlay(container: Document | HTMLElement): boolean {
    if (this.overlay?.isConnected) return true

    const overlay = container.querySelector<HTMLElement>('.hg-article-modal')
      || document.querySelector<HTMLElement>('.hg-article-modal')
    if (!overlay) return false

    const categoriesEl = overlay.querySelector<HTMLElement>('.hg-article-modal__categories')
    const scrollZone = overlay.querySelector<HTMLElement>('.hg-article-modal__scroll')

    if (!categoriesEl || !scrollZone) return false

    this.moveOverlayToBody(overlay)
    this.overlay = overlay
    this.categoriesEl = categoriesEl
    this.scrollZone = scrollZone

    this.closeEls = Array.from(overlay.querySelectorAll<HTMLElement>('[data-hg-article-modal-close]'))
    this.closeEls.forEach((el) => {
      el.addEventListener('click', this.onCloseClick)
    })
    document.addEventListener('keydown', this.onKeydown)

    return true
  }

  private moveOverlayToBody(overlay: HTMLElement) {
    document.querySelectorAll<HTMLElement>('.hg-article-modal').forEach((el) => {
      if (el !== overlay && el.parentElement === document.body) {
        el.parentElement.removeChild(el)
      }
    })

    if (overlay.parentElement !== document.body) {
      document.body.appendChild(overlay)
    }
  }

  private removeBodyOverlays() {
    document.querySelectorAll<HTMLElement>('.hg-article-modal').forEach((overlay) => {
      if (overlay.parentElement === document.body) {
        overlay.parentElement.removeChild(overlay)
      }
    })
  }

  private onTriggerClick = (e: Event) => {
    e.preventDefault()
    const trigger = e.currentTarget as HTMLElement
    const postId = trigger.dataset.postId
    if (!postId) return

    this.lastFocus = document.activeElement as HTMLElement | null
    this.openWithLoading()
    void this.fetchArticle(postId)
  }

  private openWithLoading() {
    if (!this.overlay || !this.categoriesEl || !this.scrollZone) return

    this.overlay.classList.add('is-loading')
    this.categoriesEl.innerHTML = ''
    this.scrollZone.innerHTML = `
      <div class="hg-article-modal__body">
        <h2 class="hg-article-modal__title" id="${TITLE_ID}"></h2>
        <div class="hg-article-modal__loading" role="status" aria-label="Cargando artículo">
          <span class="hg-spinner" aria-hidden="true"></span>
        </div>
      </div>
    `
    this.open()
  }

  private async fetchArticle(postId: string) {
    const settings = wp_settings
    if (!settings?.ajaxUrl || !settings?.articleModal) {
      this.renderError()
      return
    }

    const body = new URLSearchParams({
      action: settings.articleModal.action,
      nonce: settings.articleModal.nonce,
      post_id: postId,
    })

    try {
      const res = await fetch(settings.ajaxUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const json = await res.json()
      if (!json.success) throw new Error(json.data?.message || 'Error')
      this.renderContent(json.data as ArticleResponse)
    } catch {
      this.renderError()
    }
  }

  private renderContent(data: ArticleResponse) {
    if (!this.overlay || !this.categoriesEl || !this.scrollZone) return

    this.overlay.classList.remove('is-loading')
    this.categoriesEl.innerHTML = data.categories
      .map(
        (cat) =>
          `<span class="hg-article-modal__chip">${this.escHtml(cat.name)}</span>`
      )
      .join('')

    const imageMarkup = data.image_html
      ? `<figure class="hg-article-modal__media">${data.image_html}</figure>`
      : ''

    this.scrollZone.innerHTML = `
      <div class="hg-article-modal__body">
        <h2 class="hg-article-modal__title" id="${TITLE_ID}">${this.escHtml(data.title)}</h2>
        ${imageMarkup}
        <div class="hg-article-modal__text">${data.content_html}</div>
      </div>
    `
    this.scrollZone.scrollTop = 0
  }

  private renderError() {
    if (!this.overlay || !this.scrollZone) return
    this.overlay.classList.add('is-loading')
    this.scrollZone.innerHTML = `
      <div class="hg-article-modal__body">
        <h2 class="hg-article-modal__title" id="${TITLE_ID}"></h2>
        <p class="hg-article-modal__error" role="alert">No se pudo cargar el artículo. Por favor, inténtalo de nuevo.</p>
      </div>
    `
  }

  private escHtml(str: string): string {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  }

  private open() {
    if (!this.overlay) return
    this.opened = true
    this.overlay.classList.remove('is-leaving')
    this.overlay.setAttribute('aria-hidden', 'false')
    document.body.classList.add('has-article-modal-open')

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this.overlay) return
        this.overlay.classList.add('is-open')
        this.overlay.querySelector<HTMLButtonElement>('.hg-article-modal__close')?.focus()
      })
    })
  }

  private close() {
    if (!this.overlay || !this.opened || this.isLeaving) return
    this.isLeaving = true

    const backdrop = this.overlay.querySelector<HTMLElement>('.hg-article-modal__backdrop')
    let finished = false

    const finalize = () => {
      if (finished || !this.overlay) return
      finished = true
      window.clearTimeout(fallbackTimer)
      backdrop?.removeEventListener('transitionend', onTransitionEnd)

      this.overlay.classList.remove('is-open', 'is-leaving')
      this.overlay.setAttribute('aria-hidden', 'true')
      document.body.classList.remove('has-article-modal-open')
      this.opened = false
      this.isLeaving = false

      if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
        this.lastFocus.focus()
      }
    }

    const onTransitionEnd = (e: TransitionEvent) => {
      if (e.target !== backdrop || e.propertyName !== 'opacity') return
      finalize()
    }

    backdrop?.addEventListener('transitionend', onTransitionEnd)
    const fallbackTimer = window.setTimeout(finalize, 400)

    requestAnimationFrame(() => {
      this.overlay?.classList.add('is-leaving')
    })
  }

  private onCloseClick = () => this.close()

  private onKeydown = (e: KeyboardEvent) => {
    if (!this.opened) return

    if (e.key === 'Escape') {
      this.close()
      return
    }

    if (e.key === 'Tab' && this.overlay) {
      const focusables = Array.from(
        this.overlay.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => el.offsetParent !== null)

      if (focusables.length === 0) return

      const first = focusables[0]
      const last = focusables[focusables.length - 1]

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
  }
}

export default new ArticleModal()
